import { SettingOutlined } from "@ant-design/icons"
import { Button, Drawer, Form, Select } from "antd"
import React, { useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import { logout, signIn } from "../containers/LoginPage/redux/auth.thunks.ts"
import { updateUser } from "../containers/Settings/redux/settings.thunks.ts"
import { DATE_FORMATS_OBJ } from "../containers/TeamManagementPage/constants/date-formats.ts"
import { type RootState } from "../store.ts"
import { TimezoneSelect } from "./Shared/TimezoneSelect.tsx"

const DevSettingsController = ({
  userInfo,
  currentTimezone,
  updateUser,
  signIn,
  logout
}: ConnectedProps<typeof connector>) => {
  const navigate = useNavigate()
  const userId = userInfo?.id
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState<any[]>([])

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await fetch("../../dev-users.json")
        // A deployed environment with no `dev-users.json` asset falls back
        // to the SPA's own `index.html` (a 200 with `text/html`), which
        // `.json()` would otherwise try to parse and throw a confusing
        // "Unexpected token '<'" on — checked explicitly so the real cause
        // (no quick-switch fixture here) is what gets logged.
        const contentType = response.headers.get("content-type") ?? ""
        if (!response.ok || !contentType.includes("application/json")) {
          console.warn(
            "DevSettingsController: no dev-users.json in this environment — the quick user-switcher stays empty."
          )
          setUsers([])
          return
        }
        const data = await response.json()
        setUsers(data)
      } catch (error) {
        console.error("Failed to load users:", error)
        setUsers([])
      }
    }

    void loadUsers()
  }, [])

  const handleTimezoneChange = (timezone: string) => {
    if (!userId) return
    void updateUser({ id: userId, timezone })
  }

  const handleDateFormatterChange = (dateFormatter: string) => {
    if (!userId) return
    void updateUser({ id: userId, date_format: dateFormatter })
    // window.location.reload()
  }

  const handleUserChange = async (user: any) => {
    const data = {
      email: user?.username,
      password: user?.password
    }

    logout({ navigate })
    await new Promise((resolve) => setTimeout(resolve, 1000))
    signIn({ data, navigate })
  }

  return (
    <>
      <Button
        variant={"outlined"}
        color={"danger"}
        size={"large"}
        icon={<SettingOutlined />}
        onClick={() => setOpen(true)}
      />

      <Drawer
        title="Quick Settings"
        open={open}
        onClose={() => setOpen(false)}
        width={360}
      >
        <Form layout="vertical">
          <Form.Item label="Role">{userInfo?.role?.name}</Form.Item>

          <Form.Item label="Current User">
            <Select
              disabled={!users?.length}
              options={users?.map((e, i) => ({ ...e, value: i }))}
              value={
                [
                  userInfo?.first_name,
                  userInfo?.middle_name,
                  userInfo?.last_name
                ]
                  ?.filter(Boolean)
                  .join(" ") || ""
              }
              onChange={(_, o) => handleUserChange(o)}
            />
          </Form.Item>

          <Form.Item label="Time Zone">
            <TimezoneSelect
              value={currentTimezone}
              onChange={handleTimezoneChange}
            />
          </Form.Item>

          <Form.Item label="Date Format">
            <Select
              options={Object.values(DATE_FORMATS_OBJ).map((el) => ({
                ...el,
                label: el?.label,
                value: `${el?.id}`
              }))}
              value={`${userInfo?.date_format}`}
              onChange={handleDateFormatterChange}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  userInfo: state?.auth?.userInfo,
  currentTimezone: state?.settings?.user?.timezone
})

const mapDispatchToProps = {
  updateUser,
  signIn,
  logout
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DevSettingsController)
